/**
 * What the panel tells the user when no lane is delivering pixels.
 *
 * Deleting the legacy screencast fallback removes a picture that was worse than
 * nothing — it looked live and was not — but only if what replaces it actually
 * explains itself. A generic idle pane would trade a misleading picture for an
 * uninformative one, so every reason the presentation policy can produce gets
 * its own sentence, written to point at the half of the system worth checking.
 *
 * @module browserUnavailableCopy
 */
import type { BrowserPresentationUnavailableReason } from "@cmmd-forge/shared/browserPresentation";

/**
 * The reasons the panel's display-derived view model cannot work out for itself.
 *
 * `previewVm` reads the display session's own `status` and `reason`, so it
 * already has better, more specific copy for anything wrong with the *display*
 * — a failed navigation, a pending capture, a dead transport. Those must keep
 * winning; overriding them with a coarser sentence loses information.
 *
 * These two are different in kind: they are facts about the **client**, not the
 * display. A healthy, connected session still cannot be shown if this client
 * cannot host it, or if DOM is layered over the panel — and to a view model
 * looking only at the display, both look perfectly fine, which is exactly how
 * they used to render as a blank pane.
 */
const CLIENT_DERIVED_REASONS = new Set<BrowserPresentationUnavailableReason>([
  "client_cannot_host_native",
  "obscured_by_host_overlay",
]);

/** Whether the policy's reason knows something the display-derived copy cannot. */
export function browserUnavailableCopyOverridesDisplayCopy(
  reason: BrowserPresentationUnavailableReason | undefined,
): boolean {
  return reason !== undefined && CLIENT_DERIVED_REASONS.has(reason);
}

export interface BrowserUnavailableCopy {
  readonly title: string;
  readonly description: string;
}

export function browserUnavailableCopy(
  reason: BrowserPresentationUnavailableReason,
): BrowserUnavailableCopy {
  switch (reason) {
    case "no_display_session":
      return {
        title: "No live display yet",
        description: "This tab has no display attached. Open a page to start one.",
      };
    case "display_failed":
      return {
        title: "Live display failed",
        description:
          "The browser is still running, but its display stopped. Reload the tab to attach a new one.",
      };
    case "display_unsupported":
      return {
        title: "Live display unsupported",
        description: "This environment cannot provide a live browser display.",
      };
    case "network_unreachable":
      return {
        title: "Live display unreachable",
        description:
          "The video could not find a network route out of the Environment. This usually means no TURN relay is configured.",
      };
    case "unsupported_host":
      return {
        title: "Live display unsupported here",
        description: "This app cannot play the live browser stream on this host.",
      };
    case "client_cannot_host_native":
      return {
        title: "Open this in the desktop app",
        description:
          "This browser is running on your machine, so only the desktop app can show it. A web tab has no way to reach it.",
      };
    case "obscured_by_host_overlay":
      return {
        title: "Browser hidden",
        description:
          "The live view is tucked away while this panel is covered. Close it to return.",
      };
  }
}

/**
 * The title and description the status surface should actually show.
 *
 * Folds the "does the policy know better?" question and the copy lookup into
 * one call so the component does not have to hold either. That keeps the
 * decision beside the two rules it depends on — which reasons are
 * client-derived, and what each one says — and out of a React component where
 * it would be reachable only through a rendered tree.
 */
export function browserStatusCopy(input: {
  readonly reason: BrowserPresentationUnavailableReason | undefined;
  /** What the display-derived view model would say. Wins unless the reason knows better. */
  readonly title: string;
  readonly description: string;
}): BrowserUnavailableCopy {
  if (!browserUnavailableCopyOverridesDisplayCopy(input.reason) || input.reason === undefined) {
    return { title: input.title, description: input.description };
  }
  return browserUnavailableCopy(input.reason);
}
